import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { Modal } from '@mantine/core';

import { useEditProphetPage } from '../../api/editPage';
import { toApiLimit } from '../../hooks/useProphetPageForm';

import { PageForm } from './PageForm';

import { prophetTypes } from '@/features/prophet';
import { Departments, Platforms } from '@/types';

type Props = {
  page: prophetTypes.Page;
  platform: Platforms;
  department: Departments;
};

export const EditModal = NiceModal.create(({ page, platform, department }: Props) => {
  const modal = useModal();

  const { mutate, isLoading } = useEditProphetPage({ onSuccess: modal.hide });

  // Legacy pages have no isNightModeEnabled in the DB (absent = enabled); the
  // form materializes it as true. Sending that unconditionally would write a
  // phantom "— → вкл" audit record on the first edit of every such page, so
  // an unchanged value is omitted from the PATCH ("keep as is" on the server).
  const initialNightMode = page.config.isNightModeEnabled ?? true;

  return (
    <Modal
      centered
      overlayProps={{ opacity: 0.4, blur: 1.5 }}
      opened={modal.visible}
      onClose={() => modal.remove()}
      title="Edit page"
    >
      <PageForm
        mode="edit"
        onSubmit={(data) =>
          mutate({
            _id: page._id,
            funnels: data.funnels,
            // A cleared field is undefined in the form; the server treats an
            // omitted key as "keep as is", so send an explicit null to clear.
            aiId: data.aiId ?? null,
            isNightModeEnabled:
              data.isNightModeEnabled === initialNightMode ? undefined : data.isNightModeEnabled,
            limits: {
              perMinute: toApiLimit(data.limits.perMinute),
              perHour: toApiLimit(data.limits.perHour),
              perDay: toApiLimit(data.limits.perDay),
            },
          })
        }
        isLoading={isLoading}
        initialValues={{
          appPage: page.appPage,
          funnels: page.funnels,
          aiId: page.config.aiId,
          isNightModeEnabled: page.config.isNightModeEnabled ?? true,
          limits: {
            perMinute: page.config.limits?.perMinute ?? '',
            perHour: page.config.limits?.perHour ?? '',
            perDay: page.config.limits?.perDay ?? '',
          },
        }}
        department={department}
        platform={platform}
      />
    </Modal>
  );
});
